import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FiLock } from 'react-icons/fi';
import axios from 'axios';
import { queryClient } from '../lib/queryClient';
import { createForceLogout } from '../utils/forceLogout';

const AUTO_LOGOUT_SECONDS = 50;

const BlockedUserSocketModal: React.FC = () => {
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [message, setMessage] = useState('המשתמש נחסם. אתה תתנתק מהמערכת.');
  const [countdown, setCountdown] = useState(AUTO_LOGOUT_SECONDS);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Listen for user:blocked window event from SocketBridge or ClientContext
    const handleBlockedEvent = (event: CustomEvent) => {
      const payload = event.detail;
      
      if (payload?.isBlocked === true) {
        // Get current user ID to verify if this event is for us
        const currentClient = (() => {
          try {
            return JSON.parse(localStorage.getItem('client') || '{}');
          } catch {
            return {};
          }
        })();
        
        const currentUserId = String(currentClient?._id || '');
        const blockedUserId = String(payload?.userId || '');
        
        // Show modal if:
        // 1. userId matches current user, OR
        // 2. userId is null/empty (meaning it's for the current logged-in user)
        if (!blockedUserId || blockedUserId === currentUserId || !currentUserId) {
          setMessage(payload?.message || 'המשתמש נחסם. יש לפנות למנהל המערכת.');
          setShowModal(true);
          setCountdown(AUTO_LOGOUT_SECONDS);
        }
      }
    };

    window.addEventListener('user:blocked' as any, handleBlockedEvent as EventListener);

    return () => {
      window.removeEventListener('user:blocked' as any, handleBlockedEvent as EventListener);
      // Cleanup timers
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
  }, []);

  // Start countdown timer when modal shows
  useEffect(() => {
    if (showModal && !isLoggingOut) {
      // Clear any existing timers
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }

      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Countdown reached 0, trigger auto-logout
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
            }
            // Call handleLogout directly
            setIsLoggingOut(true);
            
            // CRITICAL: Set manual logout flag BEFORE clearing data
            const logoutTimestamp = Date.now().toString();
            sessionStorage.setItem('manualLogout', 'true');
            localStorage.setItem('manualLogout', 'true');
            sessionStorage.setItem('manualLogoutTime', logoutTimestamp);
            localStorage.setItem('manualLogoutTime', logoutTimestamp);
            
            // CRITICAL: Clear justLoggedIn flag to prevent interceptor from ignoring 401 errors
            sessionStorage.removeItem('justLoggedIn');
            sessionStorage.removeItem('justLoggedInTime');
            sessionStorage.removeItem('justLoggedInCompany');
            
            // 🔥 CRITICAL: Use forceLogout with 'blocked' reason (the ONLY valid auto-logout)
            console.warn('[FORCE-LOGOUT] Triggered by BlockedUserSocketModal countdown', {
              source: 'BlockedUserSocketModal.countdown',
              reason: 'blocked',
              pathname: location.pathname,
              company: location.pathname.split('/').filter(Boolean)[0] || 'test',
            });
            
            // Use forceLogout with 'blocked' reason - this is the ONLY valid auto-logout scenario
            const pathParts = location.pathname.split('/').filter(Boolean);
            const company = pathParts[0] || 'test';
            const forceLogout = createForceLogout(queryClient);
            forceLogout({
              reason: 'blocked',
              source: 'BlockedUserSocketModal.countdown',
              companySlug: company,
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Set auto-logout timeout as backup
      logoutTimeoutRef.current = setTimeout(() => {
        if (!isLoggingOut) {
          // 🔥 CRITICAL: Use forceLogout with 'blocked' reason (the ONLY valid auto-logout)
          console.warn('[FORCE-LOGOUT] Triggered by BlockedUserSocketModal timeout', {
            source: 'BlockedUserSocketModal.timeout',
            reason: 'blocked',
            timeoutSeconds: AUTO_LOGOUT_SECONDS,
            pathname: location.pathname,
            company: location.pathname.split('/').filter(Boolean)[0] || 'test',
          });
          
          setIsLoggingOut(true);
          const pathParts = location.pathname.split('/').filter(Boolean);
          const company = pathParts[0] || 'test';
          const forceLogout = createForceLogout(queryClient);
          forceLogout({
            reason: 'blocked',
            source: 'BlockedUserSocketModal.timeout',
            companySlug: company,
          });
        }
      }, AUTO_LOGOUT_SECONDS * 1000);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, isLoggingOut]);

  const handleLogout = async () => {
    // Prevent multiple calls
    if (isLoggingOut) return;
    

    
    setIsLoggingOut(true);
    
    // CRITICAL: Set manual logout flag BEFORE clearing data
    const logoutTimestamp = Date.now().toString();
    sessionStorage.setItem('manualLogout', 'true');
    localStorage.setItem('manualLogout', 'true');
    sessionStorage.setItem('manualLogoutTime', logoutTimestamp);
    localStorage.setItem('manualLogoutTime', logoutTimestamp);
    
    // CRITICAL: Set logoutReason so Login page knows to perform cleanup
    sessionStorage.setItem('logoutReason', 'manual-logout');
    
    // CRITICAL: Clear justLoggedIn flag to prevent interceptor from ignoring 401 errors
    sessionStorage.removeItem('justLoggedIn');
    sessionStorage.removeItem('justLoggedInTime');
    sessionStorage.removeItem('justLoggedInCompany');
    
    // Clear timers
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }

    try {
      // IMPORTANT: Clear all React Query cache and CRM-related localStorage on logout
      // to ensure next login starts from a clean slate
      // 1) Clear all React Query caches (queries + mutations)
      queryClient.clear();
      
      // 2) Clear auth snapshot (in-memory auth state)
      try {
        const { clearAuthSnapshot } = await import('../prefetch/api');
        clearAuthSnapshot();
      } catch (err) {
        console.error('[BlockedUserSocketModal] Error clearing auth snapshot:', err);
      }
      
      // 3) Clear all CRM-related auth and company data from localStorage
      // Keep user preferences like rememberEmail, rememberPassword, device_id
      const oldClient = localStorage.getItem('client');
      if (oldClient) {
        try {
          const parsed = JSON.parse(oldClient);
        } catch (e) {
          console.warn('[BlockedUserSocketModal] Could not parse client data');
        }
      }
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('client');
      localStorage.removeItem('currentCompany');
      localStorage.removeItem('activeTenantId');
      localStorage.removeItem('activeTenantName');
      localStorage.removeItem('activeRole');
      localStorage.removeItem('lastLogin');
      localStorage.removeItem('lastActivity');
      localStorage.removeItem('wjs_active_company_v2'); // New company slug storage key
      localStorage.removeItem('wjs_superadmin_auth'); // Superadmin-specific auth (if exists)
      
      // 3.5) CRITICAL: Clear cookies that might persist
      // Use centralized auth cookie clear helper so flags (domain, secure, sameSite)
      // match the backend's setAuthCookie/clearAuthCookie behavior.
      if (typeof document !== 'undefined') {
        import('../utils/authCookies').then(({ clearAllAuthCookies }) => {
          clearAllAuthCookies();
        }).catch(err => {
          console.error('[BlockedUserSocketModal] Error importing authCookies:', err);
        });
      }
      
      // 4) Clear axios default headers
      delete axios.defaults.headers.common.Authorization;
      
      // Get company from pathname
      const pathParts = location.pathname.split('/').filter(Boolean);
      const company = pathParts[0] || 'test';
      
      // Disconnect any socket connections
      // (SocketBridge will handle this, but we ensure it's done)
      
      // Use forceLogout with manual_click reason (user clicked logout button)
      const forceLogout = createForceLogout(queryClient);
      setTimeout(() => {
        forceLogout({
          reason: 'manual_click',
          source: 'BlockedUserSocketModal.handleLogout',
          companySlug: company,
        });
      }, 500);
    } catch (error) {
      // 🔥 CRITICAL: Log error during logout
      console.warn('[FORCE-LOGOUT] Error during BlockedUserSocketModal logout, redirecting anyway', {
        source: 'BlockedUserSocketModal.handleLogout.error',
        reason: 'user-blocked-error-during-logout',
        error: error instanceof Error ? error.message : String(error),
        pathname: location.pathname,
      });
      
      // Even if there's an error, try to redirect
      const pathParts = location.pathname.split('/').filter(Boolean);
      const company = pathParts[0] || 'test';
      window.location.href = `/${company}/login`;
    }
  };

  if (!showModal) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4" 
      dir="rtl"
      onClick={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        // Prevent closing with ESC
        if (e.key === 'Escape') e.preventDefault();
      }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <FiLock className="w-8 h-8 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
          {message.includes('חברה') ? 'החברה נחסמה' : 'המשתמש נחסם'}
        </h2>

        {/* Message */}
        <div className="text-center text-gray-700 mb-6 space-y-3">
          <p className="text-base font-semibold">
            {message.includes('חברה') 
              ? 'החברה שלך נחסמה מהמערכת'
              : 'המשתמש שלך נחסם מהמערכת'}
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            {message.includes('חברה')
              ? 'כל המשתמשים של החברה הזו נחסמו ואין לך הרשאה להמשיך להשתמש במערכת כרגע.'
              : 'אין לך הרשאה להמשיך להשתמש במערכת כרגע.'}
          </p>
          <p className="text-sm text-gray-700 font-medium">
            {message.includes('חברה')
              ? 'אנא פנה לסופר אדמין למידע נוסף ולשחרור החסימה.'
              : 'אנא פנה למנהל המערכת למידע נוסף ולשחרור החסימה.'}
          </p>
        </div>

        {/* Countdown Timer */}
        <div className="mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-red-700 font-medium">
                יתנתק אוטומטית בעוד:
              </span>
              <span className={`text-2xl font-bold ${
                countdown <= 10 ? 'text-red-600 animate-pulse' : 'text-red-700'
              }`}>
                {countdown}
              </span>
              <span className="text-sm text-red-700 font-medium">
                שניות
              </span>
            </div>
          </div>
        </div>

        {/* Button */}
        <div className="flex justify-center">
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={`px-8 py-3 rounded-xl font-bold text-white shadow-lg transition-all ${
              isLoggingOut
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 hover:shadow-xl active:scale-95'
            }`}
          >
            {isLoggingOut ? 'מתנתק...' : 'הבנתי'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockedUserSocketModal;

